import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Pressable } from '../pressable'
import { PencilGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'

export type UnitTaskNoteProps = {
    /** The note body (Figma shows multi-line free text). */
    text?: string
    /** Render the edit pencil affordance. */
    editable?: boolean
    onEdit?: () => void
    /** Slot for richer note content instead of plain `text`. */
    children?: React.ReactNode
    className?: string
    testID?: string
}

/**
 * Unit Task List additional-note box (Figma `UnitTaskListMessage`, node
 * 10706:125860): a dark `bg/input` panel with a hairline `border/subtle`,
 * holding the note text with an optional edit pencil pinned top-right. Replaces
 * the legacy white note box. P4 muted text matches the Figma "Buses: /
 * Emergency cars:" sample.
 */
export const UnitTaskNote: React.FC<UnitTaskNoteProps> = ({
    text,
    editable = false,
    onEdit,
    children,
    className,
    testID,
}) => (
    <Box
        className={twMerge(
            'flex-row items-start justify-between gap-2 rounded border border-solid border-border-subtle bg-aos-bg-input p-3',
            className,
        )}
        testID={testID}
    >
        <Box className='min-w-0 flex-1'>
            {children ?? (
                <Text size='caption' tone='default'>
                    {text}
                </Text>
            )}
        </Box>
        {editable ? (
            <Pressable
                onPress={onEdit}
                className='shrink-0 rounded p-0.5 hover:bg-aos-bg-hover-layer'
                accessibilityRole='button'
                accessibilityLabel='Edit note'
            >
                <PencilGlyph size={16} />
            </Pressable>
        ) : null}
    </Box>
)

UnitTaskNote.displayName = 'UnitTaskNote'
